import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { Employee } from '@/entities/employee'
import { employeesQueryOptions } from '@/entities/employee'
import { type Excursion, excursionQueryOptions } from '@/entities/excursion'
import { useSession } from '@/entities/session'
import {
  compareByTime,
  scheduleQueryOptions,
  type Task,
  TaskRow,
  useUpdateTaskMutation,
} from '@/entities/task'
import { BookingList } from '@/features/booking-manager'
import type { ExcursionDialogState } from '@/features/excursion-editor'
import { ExcursionDialog, ExcursionLifecycle } from '@/features/excursion-editor'
import { TaskDialog, type TaskDialogState } from '@/features/task-editor'
import { errorMessage } from '@/shared/api'
import { addDays, fmtInterval, startOfDay } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'
import { PageBody } from '@/shared/ui/page-body'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'

export const Route = createFileRoute('/_app/excursions/$excursionId')({
  component: ExcursionPage,
})

function ExcursionPage() {
  const { excursionId } = Route.useParams()
  const { session } = useSession()
  const excursionQuery = useQuery(excursionQueryOptions(excursionId))
  const employeesQuery = useQuery(employeesQueryOptions(true))
  const [dialog, setDialog] = useState<ExcursionDialogState | null>(null)

  if (excursionQuery.isLoading) return <Spinner className="m-8" />
  if (excursionQuery.isError) {
    return <StatusScreen title="Выход не открылся" message={errorMessage(excursionQuery.error)} />
  }

  const excursion = excursionQuery.data
  if (!excursion) return null
  const employees = employeesQuery.data ?? []
  // Готовую ссылку собирает бэкенд: секрет наружу не отдаётся вовсе.
  const link = excursion.publicUrl

  return (
    <PageBody className="gap-6">
      <PageHeader
        eyebrow={fmtInterval(excursion.slot)}
        title={excursion.title}
        // Занятость ушла в карточку деталей: голое «4/10» над заголовком
        // читалось как обрывок, а рядом с «записано из» — как факт о выходе.
        status={
          (excursion.state === 'cancelled' || excursion.state === 'held') && (
            <div className="flex flex-wrap items-center gap-2">
              {excursion.state === 'cancelled' && <Chip tone="accent">отменён</Chip>}
              {excursion.state === 'held' && <Chip tone="success">состоялся</Chip>}
            </div>
          )
        }
        actions={
          session?.isAdmin && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setDialog({ excursion, slot: excursion.slot, guideIds: excursion.guideIds })
              }
            >
              Править
            </Button>
          )
        }
      />

      {excursion.cancellationReason && (
        <p className="text-sm text-warning">Причина отмены: {excursion.cancellationReason}</p>
      )}

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Icon name="pin" size={16} />
          {excursion.meetingPoint ?? 'Точка сбора не указана'}
        </div>
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Icon name="user" size={16} />
          {excursion.guideIds
            .map((id) => employees.find((e) => e.id === id)?.name ?? 'неизвестный')
            .join(', ') || 'гид не назначен'}
        </div>
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Icon name="users" size={16} />
          <span>
            Записано{' '}
            <span className="mono-num text-text-primary">
              {excursion.capacity === null
                ? excursion.seatsTaken
                : `${excursion.seatsTaken} из ${excursion.capacity}`}
            </span>
            {excursion.capacity === null && ' — без ограничения мест'}
          </span>
        </div>
        {link && (
          <div className="flex flex-wrap items-center gap-2">
            <Icon name="link" size={16} className="shrink-0 text-text-secondary" />
            <code
              title={link}
              className="mono-num min-w-0 flex-1 truncate text-xs text-text-tertiary"
            >
              {link}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(link)
                  .then(() => toast.success('Ссылка скопирована'))
              }}
            >
              Скопировать
            </Button>
          </div>
        )}
      </Card>

      <ExcursionLifecycle excursion={excursion} />

      <BookingList excursion={excursion} />

      <ExcursionTasks excursion={excursion} employees={employees} />

      <ExcursionDialog state={dialog} employees={employees} onClose={() => setDialog(null)} />
    </PageBody>
  )
}

interface ExcursionTasksProps {
  excursion: Excursion
  employees: Employee[]
}

/**
 * Дела выхода. Ручки «дела по выходу» нет — берём расписание его суток вместе
 * с делами без времени и отбираем привязанные: подготовка к выходу живёт в тот
 * же день, а бессрочное — в группе без времени.
 */
function ExcursionTasks({ excursion, employees }: ExcursionTasksProps) {
  const { session } = useSession()
  const from = startOfDay(excursion.slot.startsAt)
  const to = addDays(startOfDay(excursion.slot.endsAt), 1)
  const scheduleQuery = useQuery(scheduleQueryOptions({ from, to, includeUntimed: true }))
  const updateTask = useUpdateTaskMutation()
  const [dialog, setDialog] = useState<TaskDialogState | null>(null)

  const schedule = scheduleQuery.data
  const tasks = [...(schedule?.items ?? []), ...(schedule?.untimed ?? [])]
    .filter((task) => task.excursionId === excursion.id)
    .sort(compareByTime)

  const openTask = (task: Task) => {
    setDialog({
      task,
      slot: task.slot,
      assigneeIds: task.assigneeIds,
      excursionId: task.excursionId,
    })
  }

  return (
    <section aria-label="Дела выхода" className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="label">Дела</h2>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            setDialog({
              task: null,
              slot: null,
              // Гид заводит дела только себе — на всех ведущих ставит
              // администратор.
              assigneeIds:
                session?.isAdmin === false && session.employeeId
                  ? [session.employeeId]
                  : excursion.guideIds,
              excursionId: excursion.id,
            })
          }
        >
          <Icon name="plus" size={14} />
          дело
        </Button>
      </div>
      {tasks.length === 0 ? (
        <p className="text-sm text-text-tertiary">К этому выходу дел нет.</p>
      ) : (
        <ul className="flex flex-col">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onOpen={openTask}
              onToggle={() => updateTask.mutate({ id: task.id, patch: { done: !task.done } })}
            />
          ))}
        </ul>
      )}

      <TaskDialog
        state={dialog}
        employees={employees}
        excursions={[excursion]}
        day={from}
        onClose={() => setDialog(null)}
      />
    </section>
  )
}
